import { getAuthentication } from "../../lib/auth";
import { ARTICLES_URL } from "../../lib/env";
import { gqlRequest } from "../../lib/functions";
import { AISearchResponseGqlSchema } from "../../lib/gql/AISearchResponse";
import { articleGQLSchema } from "../../lib/gql/Article";
import { IIndexStatsGqlSchema } from "../../lib/gql/IIndexStats";
import { IAISearchResponse } from "../../types/AISearchResponse";
import { IArticle } from "../../types/Article";
import { IIndexStats } from "../../types/IIndexStats";

export async function filterArticles(
  limit: number,
  offset: number,
  query?: string,
  category?: string | null
): Promise<IArticle[]> {
  let filters: string = "";
  if (query && query.trim() !== "") {
    // Use JSON.stringify to properly escape query parameter for GraphQL
    const escapedQuery = JSON.stringify(query).slice(1, -1);
    filters += `,query:"${escapedQuery}"`;
  }
  if (category && category.trim() !== "") {
    const escapedCategory = JSON.stringify(category).slice(1, -1);
    filters += `,category:"${escapedCategory}"`;
  }
  const gqlString = `query{
        FilterArticles(limit:${limit},offset:${offset}${filters}){        
            ${articleGQLSchema}
        }
    }`;

  try {
    const response = await gqlRequest<{
      FilterArticles: IArticle[];
    }>({
      query: gqlString,
      endpoint: ARTICLES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      return [];
    }
    if (response.data?.FilterArticles) {
      return response.data.FilterArticles;
    }
    return [];
  } catch (err) {
    throw err;
  }
}

export async function getArticleByID(id: string): Promise<IArticle | null> {
  const gqlString = `query{
    GetArticleByID(id:"${id}"){
      ${articleGQLSchema}
    }
  }`;
  try {
    const response = await gqlRequest<{
      GetArticleByID: IArticle;
    }>({
      query: gqlString,
      endpoint: ARTICLES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      return null;
    }
    if (response.data?.GetArticleByID) {
      return response.data.GetArticleByID;
    }
    return null;
  } catch (err) {
    throw err;
  }
}

export async function searchArticleAI(
  query: string,
  limit: number = 5
): Promise<IAISearchResponse | null> {
  // Use JSON.stringify to properly escape query parameter for GraphQL
  const escapedQuery = JSON.stringify(query).slice(1, -1);

  const gqlString = `query{
    SearchArticleAI(query:"${escapedQuery}",limit:${limit}){
      ${AISearchResponseGqlSchema}
    }
  }`;
  try {
    const response = await gqlRequest<{
      SearchArticleAI: IAISearchResponse;
    }>({
      query: gqlString,
      endpoint: ARTICLES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      return null;
    }
    if (response.data?.SearchArticleAI) {
      return response.data.SearchArticleAI;
    }
    return null;
  } catch (err) {
    throw err;
  }
}

export async function getIndexStats(): Promise<IIndexStats | null> {
  const gqlString = `query{
    GetIndexStats{
      ${IIndexStatsGqlSchema}
    }
  }`;
  try {
    const response = await gqlRequest<{
      GetIndexStats: IIndexStats;
    }>({
      query: gqlString,
      endpoint: ARTICLES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      return null;
    }
    if (response.data?.GetIndexStats) {
      return response.data.GetIndexStats;
    }
    return null;
  } catch (err) {
    throw err;
  }
}

export async function addNewArticle(
  title: string,
  category: string,
  author?: string,
  source_name?: string,
  source_link?: string
): Promise<IArticle | null> {
  // Use JSON.stringify to properly escape all string parameters for GraphQL
  const escapedTitle = JSON.stringify(title).slice(1, -1);
  const escapedCategory = JSON.stringify(category).slice(1, -1);
  const escapedAuthor = JSON.stringify(author || "").slice(1, -1);
  const escapedSourceName = JSON.stringify(source_name || "").slice(1, -1);
  const escapedSourceLink = JSON.stringify(source_link || "").slice(1, -1);

  const gqlString = `mutation{
    AddNewArticle(title:"${escapedTitle}",category:"${escapedCategory}",author:"${escapedAuthor}",source_name:"${escapedSourceName}",source_link:"${escapedSourceLink}"){
      ${articleGQLSchema}
    }
  }`;
  try {
    const response = await gqlRequest<{
      AddNewArticle: IArticle;
    }>({
      query: gqlString,
      endpoint: ARTICLES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      return null;
    }
    if (response.data?.AddNewArticle) {
      return response.data.AddNewArticle;
    }
    return null;
  } catch (err) {
    throw err;
  }
}

export async function editArticleSeo(
  id: string,
  keywords: string[],
  description: string
): Promise<IArticle | null> {
  // Use JSON.stringify to properly escape strings for GraphQL
  const escapedDescription = JSON.stringify(description).slice(1, -1);
  const escapedKeywords = keywords
    .map((keyword) => JSON.stringify(keyword).slice(1, -1))
    .join(";");

  const gqlString = `mutation{
    EditArticleSeo(id:"${id}",description:"${escapedDescription}",keywords:"${escapedKeywords}"){
      ${articleGQLSchema}
    }
  }`;
  try {
    const response = await gqlRequest<{
      EditArticleSeo: IArticle;
    }>({
      query: gqlString,
      endpoint: ARTICLES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      return null;
    }
    if (response.data?.EditArticleSeo) {
      return response.data.EditArticleSeo;
    }
    return null;
  } catch (err) {
    throw err;
  }
}

export async function editArticleMainInfo(
  id: string,
  title: string,
  category: string,
  author: string,
  source_name: string,
  source_link: string
): Promise<IArticle | null> {
  const escapedTitle = title.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  const escapedCategory = category.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  const escapedAuthor = author.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  const escapedSourceName = source_name
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"');
  const escapedSourceLink = source_link
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"');

  const gqlString = `mutation{
    EditArticleMainInfo(id:"${id}",title:"${escapedTitle}",category:"${escapedCategory}",author:"${escapedAuthor}",source_name:"${escapedSourceName}",source_link:"${escapedSourceLink}"){
      ${articleGQLSchema}
    }
  }`;
  try {
    const response = await gqlRequest<{
      EditArticleMainInfo: IArticle;
    }>({
      query: gqlString,
      endpoint: ARTICLES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      return null;
    }
    if (response.data?.EditArticleMainInfo) {
      return response.data.EditArticleMainInfo;
    }
    return null;
  } catch (err) {
    throw err;
  }
}

export async function editArticleMainImage(
  id: string,
  filepath: string,
  alt: string
): Promise<IArticle | null> {
  // Use JSON.stringify to properly escape string parameters for GraphQL
  const escapedFilepath = JSON.stringify(filepath).slice(1, -1);
  const escapedAlt = JSON.stringify(alt).slice(1, -1);

  const gqlString = `mutation{
    EditArticleMainImage(id:"${id}",filepath:"${escapedFilepath}",alt:"${escapedAlt}"){
      ${articleGQLSchema}
    }
  }`;
  try {
    const response = await gqlRequest<{
      EditArticleMainImage: IArticle;
    }>({
      query: gqlString,
      endpoint: ARTICLES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      return null;
    }
    if (response.data?.EditArticleMainImage) {
      return response.data.EditArticleMainImage;
    }
    return null;
  } catch (err) {
    throw err;
  }
}

export async function editArticleContent(
  id: string,
  content: string
): Promise<IArticle | null> {
  // Use JSON.stringify to properly escape the content, then remove outer quotes
  const escapedContent = JSON.stringify(content).slice(1, -1);

  const gqlString = `mutation{
    EditArticleContent(id:"${id}",content:"${escapedContent}"){
      ${articleGQLSchema}
    }
  }`;

  try {
    const response = await gqlRequest<{
      EditArticleContent: IArticle;
    }>({
      query: gqlString,
      endpoint: ARTICLES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      return null;
    }
    if (response.data?.EditArticleContent) {
      return response.data.EditArticleContent;
    }
    return null;
  } catch (err) {
    throw err;
  }
}

export async function pushinArticleAttachment(
  id: string,
  filepath: string,
  alt: string
): Promise<IArticle | null> {
  // Use JSON.stringify to properly escape string parameters for GraphQL
  const escapedFilepath = JSON.stringify(filepath).slice(1, -1);
  const escapedAlt = JSON.stringify(alt).slice(1, -1);

  const gqlString = `mutation{
    PushinArticleAttachment(id:"${id}",filepath:"${escapedFilepath}",alt:"${escapedAlt}"){
      ${articleGQLSchema}
    }
  }`;
  try {
    const response = await gqlRequest<{
      PushinArticleAttachment: IArticle;
    }>({
      query: gqlString,
      endpoint: ARTICLES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      return null;
    }
    if (response.data?.PushinArticleAttachment) {
      return response.data.PushinArticleAttachment;
    }
    return null;
  } catch (err) {
    throw err;
  }
}

export async function pullArticleAttachment(
  id: string,
  attachmentId: string
): Promise<IArticle | null> {
  const gqlString = `mutation{
    PullArticleAttachment(id:"${id}",attachmentId:"${attachmentId}"){
      ${articleGQLSchema}
    }
  }`;
  try {
    const response = await gqlRequest<{
      PullArticleAttachment: IArticle;
    }>({
      query: gqlString,
      endpoint: ARTICLES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      return null;
    }
    if (response.data?.PullArticleAttachment) {
      return response.data.PullArticleAttachment;
    }
    return null;
  } catch (err) {
    throw err;
  }
}

export async function markArticleAsEdited(
  id: string
): Promise<IArticle | null> {
  const gqlString = `mutation{
    MarkArticleAsEdited(id:"${id}"){
      ${articleGQLSchema}
    }
  }`;
  try {
    const response = await gqlRequest<{
      MarkArticleAsEdited: IArticle;
    }>({
      query: gqlString,
      endpoint: ARTICLES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      return null;
    }
    if (response.data?.MarkArticleAsEdited) {
      return response.data.MarkArticleAsEdited;
    }
    return null;
  } catch (err) {
    throw err;
  }
}

export async function addToMainNews(
  id: string,
  type: "LARGE" | "SMALL"
): Promise<boolean> {
  const gqlString = `mutation{
    AddToMainNews(id:"${id}",type:${type})
  }`;
  try {
    const response = await gqlRequest<{
      AddToMainNews: boolean;
    }>({
      query: gqlString,
      endpoint: ARTICLES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      return false;
    }
    if (response.data?.AddToMainNews) {
      return response.data.AddToMainNews;
    }
    return false;
  } catch (err) {
    throw err;
  }
}

export async function removeFromMainNews(id: string): Promise<boolean> {
  const gqlString = `mutation{
    RemoveFromMainNews(id:"${id}")
  }`;
  try {
    const response = await gqlRequest<{
      RemoveFromMainNews: boolean;
    }>({
      query: gqlString,
      endpoint: ARTICLES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      return false;
    }
    if (response.data?.RemoveFromMainNews) {
      return response.data.RemoveFromMainNews;
    }
    return false;
  } catch (err) {
    throw err;
  }
}

export async function deleteArticle(id: string): Promise<boolean> {
  const gqlString = `mutation{
    DeleteArticle(id:"${id}")
  }`;
  try {
    const response = await gqlRequest<{
      DeleteArticle: boolean;
    }>({
      query: gqlString,
      endpoint: ARTICLES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      return false;
    }
    if (response.data?.DeleteArticle) {
      return response.data.DeleteArticle;
    }
    return false;
  } catch (err) {
    throw err;
  }
}

export async function indexArticle(id: string): Promise<boolean> {
  const gqlString = `mutation{
    IndexArticle(id:"${id}")
  }`;
  try {
    const response = await gqlRequest<{
      IndexArticle: boolean;
    }>({
      query: gqlString,
      endpoint: ARTICLES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      return false;
    }
    if (response.data?.IndexArticle) {
      return response.data.IndexArticle;
    }
    return false;
  } catch (err) {
    throw err;
  }
}

export async function reindexAllArticles(): Promise<boolean> {
  const gqlString = `mutation{
    ReindexAllArticles
  }`;
  try {
    const response = await gqlRequest<{
      ReindexAllArticles: boolean;
    }>({
      query: gqlString,
      endpoint: ARTICLES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      return false;
    }
    if (response.data?.ReindexAllArticles) {
      return response.data.ReindexAllArticles;
    }
    return false;
  } catch (err) {
    throw err;
  }
}

export async function publishArticle(id: string): Promise<boolean> {
  const gqlString = `mutation{
    PublishArticle(id:"${id}")
  }`;
  try {
    const response = await gqlRequest<{
      PublishArticle: boolean;
    }>({
      query: gqlString,
      endpoint: ARTICLES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      return false;
    }
    if (response.data?.PublishArticle) {
      return response.data.PublishArticle;
    }
    return false;
  } catch (err) {
    throw err;
  }
}

export async function unpublishArticle(id: string): Promise<boolean> {
  const gqlString = `mutation{
    UnpublishArticle(id:"${id}")
  }`;
  try {
    const response = await gqlRequest<{
      UnpublishArticle: boolean;
    }>({
      query: gqlString,
      endpoint: ARTICLES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      return false;
    }
    if (response.data?.UnpublishArticle) {
      return response.data.UnpublishArticle;
    }
    return false;
  } catch (err) {
    throw err;
  }
}
